package com.mgacreative.hotelru.feature.admin.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.outlined.CorporateFare
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotelru.domain.model.Department
import com.mgacreative.hotelru.shared.ui.designsystem.components.LanguageSelectorRow
import com.mgacreative.hotelru.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

val availablePermissions = listOf(
    "AiAssistantScreen" to "AI Asistan",
    "CrmDashboardScreen" to "CRM & Misafir",
    "ChannelManagerScreen" to "Kanal Yönetimi",
    "DynamicPricingScreen" to "Dinamik Fiyatlandırma",
    "ReservationCalendarScreen" to "Rezervasyon Takvimi",
    "CreateReservationScreen" to "Yeni Rezervasyon",
    "CheckInListScreen" to "Check-in Listesi",
    "CheckOutListScreen" to "Check-out Listesi",
    "ProfitReportScreen" to "Gelir/Gider Raporu",
    "StatisticsScreen" to "İstatistikler",
    "InUseRoomsScreen" to "Kullanımdaki Odalar",
    "HousekeepingScreen" to "Housekeeping",
    "MaintenanceScreen" to "Bakım & Arıza",
    "CashRegisterScreen" to "Kasa",
    "InventoryScreen" to "Stok / Envanter",
    "RestaurantPosScreen" to "Restaurant POS",
    "ShiftPlannerScreen" to "Vardiya Planlama",
    "StaffListScreen" to "Personel Yönetimi",
    "AdminDashboardScreen" to "Sistem Yönetimi"
)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AdminDepartmentManagementScreen(
    onNavigateBack: () -> Unit = {},
    viewModel: AdminDepartmentViewModel = koinViewModel()
) {
    val departments by viewModel.departments.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()
    val error by viewModel.error.collectAsState()

    var showDialog by remember { mutableStateOf(false) }
    var editingDepartment by remember { mutableStateOf<Department?>(null) }
    var departmentToDelete by remember { mutableStateOf<Department?>(null) }

    var departmentName by remember { mutableStateOf("") }
    var selectedPermissions by remember { mutableStateOf<Set<String>>(emptySet()) }

    val listState = rememberLazyListState()

    LaunchedEffect(Unit) {
        viewModel.loadDepartments()
    }

    Scaffold(
        containerColor = Color(0xFFF8FAFC),
        topBar = {
            Surface(
                color = Color.White,
                shadowElevation = 1.dp
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 12.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        IconButton(onClick = onNavigateBack) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = l10n("Geri"), tint = Color(0xFF0F172A))
                        }
                        Spacer(Modifier.width(8.dp))
                        Column {
                            Text(
                                text = l10n("Departman Yönetimi"),
                                fontSize = 18.sp,
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFF0F172A)
                            )
                            Text(
                                text = "${departments.size} ${l10n("Departman Tanımlı")}",
                                fontSize = 12.sp,
                                color = Color(0xFF64748B)
                            )
                        }
                    }

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Button(
                            onClick = {
                                editingDepartment = null
                                departmentName = ""
                                selectedPermissions = emptySet()
                                showDialog = true
                            },
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                            shape = RoundedCornerShape(10.dp),
                            contentPadding = PaddingValues(horizontal = 14.dp, vertical = 8.dp)
                        ) {
                            Icon(Icons.Default.Add, contentDescription = null, tint = Color.White, modifier = Modifier.size(18.dp))
                            Spacer(Modifier.width(6.dp))
                            Text(l10n("Departman Ekle"), fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = Color.White)
                        }

                        LanguageSelectorRow(isDarkTheme = false)
                    }
                }
            }
        }
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .verticalScrollbar(listState)
        ) {
            LazyColumn(
                state = listState,
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                if (isLoading && departments.isEmpty()) {
                    item {
                        LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
                    }
                }

                if (departments.isEmpty() && !isLoading) {
                    item {
                        Box(
                            modifier = Modifier.fillMaxWidth().padding(48.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                Icon(Icons.Outlined.CorporateFare, contentDescription = null, tint = Color(0xFF94A3B8), modifier = Modifier.size(48.dp))
                                Spacer(Modifier.height(12.dp))
                                Text(l10n("Henüz tanımlı departman bulunmuyor"), fontSize = 15.sp, fontWeight = FontWeight.Bold, color = Color(0xFF0F172A))
                            }
                        }
                    }
                } else {
                    items(departments, key = { it.id }) { department ->
                        Card(
                            shape = RoundedCornerShape(16.dp),
                            colors = CardDefaults.cardColors(containerColor = Color.White),
                            elevation = CardDefaults.cardElevation(defaultElevation = 0.dp),
                            border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(16.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.SpaceBetween
                            ) {
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    modifier = Modifier.weight(1f)
                                ) {
                                    Box(
                                        modifier = Modifier
                                            .size(44.dp)
                                            .clip(CircleShape)
                                            .background(Color(0xFFF1F5F9)),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Icon(
                                            imageVector = Icons.Outlined.CorporateFare,
                                            contentDescription = null,
                                            tint = Color(0xFF475569),
                                            modifier = Modifier.size(22.dp)
                                        )
                                    }

                                    Spacer(Modifier.width(14.dp))

                                    Column {
                                        Text(
                                            text = department.name,
                                            fontSize = 15.sp,
                                            fontWeight = FontWeight.Bold,
                                            color = Color(0xFF0F172A)
                                        )
                                        Spacer(Modifier.height(3.dp))
                                        Surface(
                                            color = Color(0xFFEFF6FF),
                                            shape = RoundedCornerShape(6.dp)
                                        ) {
                                            Text(
                                                text = "${department.permissions.size} " + l10n("yetki tanımlı"),
                                                fontSize = 11.sp,
                                                fontWeight = FontWeight.SemiBold,
                                                color = Color(0xFF2563EB),
                                                modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp)
                                            )
                                        }
                                    }
                                }

                                Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                                    IconButton(
                                        onClick = {
                                            editingDepartment = department
                                            departmentName = department.name
                                            selectedPermissions = department.permissions.toSet()
                                            showDialog = true
                                        }
                                    ) {
                                        Icon(
                                            Icons.Outlined.Edit,
                                            contentDescription = l10n("Düzenle"),
                                            tint = Color(0xFF2563EB),
                                            modifier = Modifier.size(20.dp)
                                        )
                                    }
                                    IconButton(
                                        onClick = { departmentToDelete = department }
                                    ) {
                                        Icon(
                                            Icons.Outlined.Delete,
                                            contentDescription = l10n("Sil"),
                                            tint = Color(0xFFDC2626),
                                            modifier = Modifier.size(20.dp)
                                        )
                                    }
                                }
                            }
                        }
                    }
                }

                item {
                    Spacer(Modifier.height(32.dp))
                }
            }
        }

        if (showDialog) {
            AlertDialog(
                onDismissRequest = { showDialog = false },
                containerColor = Color.White,
                shape = RoundedCornerShape(20.dp),
                title = { Text(if (editingDepartment == null) l10n("Yeni Departman Ekle") else l10n("Departmanı Düzenle"), fontWeight = FontWeight.Bold, fontSize = 18.sp) },
                text = {
                    Column(
                        modifier = Modifier.fillMaxWidth().heightIn(max = 400.dp),
                        verticalArrangement = Arrangement.spacedBy(14.dp)
                    ) {
                        OutlinedTextField(
                            value = departmentName,
                            onValueChange = { departmentName = it },
                            label = { Text(l10n("Departman Adı")) },
                            singleLine = true,
                            shape = RoundedCornerShape(10.dp),
                            modifier = Modifier.fillMaxWidth()
                        )

                        Text(l10n("Yetkiler (Görebileceği Sayfalar)"), fontWeight = FontWeight.Bold, fontSize = 13.sp, color = Color(0xFF0F172A))

                        LazyColumn(
                            modifier = Modifier.fillMaxWidth().heightIn(max = 280.dp),
                            verticalArrangement = Arrangement.spacedBy(2.dp)
                        ) {
                            items(availablePermissions) { (screenId, title) ->
                                val isChecked = selectedPermissions.contains(screenId)
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clickable {
                                            selectedPermissions = if (isChecked) {
                                                selectedPermissions - screenId
                                            } else {
                                                selectedPermissions + screenId
                                            }
                                        }
                                        .padding(vertical = 4.dp),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Checkbox(
                                        checked = isChecked,
                                        onCheckedChange = { checked ->
                                            selectedPermissions = if (checked) {
                                                selectedPermissions + screenId
                                            } else {
                                                selectedPermissions - screenId
                                            }
                                        }
                                    )
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Text(l10n(title), fontSize = 13.sp, color = Color(0xFF334155))
                                }
                            }
                        }
                    }
                },
                confirmButton = {
                    Button(
                        onClick = {
                            if (departmentName.isNotBlank()) {
                                if (editingDepartment == null) {
                                    viewModel.addDepartment(departmentName.trim(), selectedPermissions.toList())
                                } else {
                                    viewModel.updateDepartment(editingDepartment!!.id, departmentName.trim(), selectedPermissions.toList())
                                }
                                showDialog = false
                            }
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                        shape = RoundedCornerShape(8.dp)
                    ) {
                        Text(l10n("Kaydet"), fontWeight = FontWeight.Bold, color = Color.White)
                    }
                },
                dismissButton = {
                    TextButton(onClick = { showDialog = false }) {
                        Text(l10n("İptal"), color = Color(0xFF64748B))
                    }
                }
            )
        }

        if (departmentToDelete != null) {
            val dep = departmentToDelete!!
            AlertDialog(
                onDismissRequest = { departmentToDelete = null },
                containerColor = Color.White,
                shape = RoundedCornerShape(20.dp),
                title = { Text(l10n("Departmanı Sil"), fontWeight = FontWeight.Bold) },
                text = { Text("${dep.name} ${l10n("departmanı kalıcı olarak silinecek. Onaylıyor musunuz?")}") },
                confirmButton = {
                    Button(
                        onClick = {
                            viewModel.deleteDepartment(dep.id)
                            departmentToDelete = null
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFDC2626)),
                        shape = RoundedCornerShape(8.dp)
                    ) {
                        Text(l10n("Sil"), color = Color.White, fontWeight = FontWeight.Bold)
                    }
                },
                dismissButton = {
                    TextButton(onClick = { departmentToDelete = null }) {
                        Text(l10n("İptal"), color = Color(0xFF64748B))
                    }
                }
            )
        }

        error?.let {
            AlertDialog(
                onDismissRequest = { viewModel.clearError() },
                containerColor = Color.White,
                shape = RoundedCornerShape(20.dp),
                title = { Text(l10n("Hata")) },
                text = { Text(it) },
                confirmButton = {
                    TextButton(onClick = { viewModel.clearError() }) { Text(l10n("Tamam")) }
                }
            )
        }
    }
}
